import qs from 'qs'

import { CpaToolsIntegrationStatus } from '@features/invoices/enums/cpaToolsIntegrationStatus'

import { Metable } from '../../types/metable'
import apiZervant from '../../utils/apiZervant'
import { ImportedInvoiceApproved } from './types/importedInvoiceApproved'
import { ImportedInvoiceDocument } from './types/importedInvoiceDocument'
import {
  ImportedInvoiceCounterparty,
  ImportedInvoiceExtractionHeader,
  ImportedInvoiceExtractionLine,
  ImportedInvoiceProposal,
} from './types/importedInvoiceExtraction'
import type { ImportedInvoiceStatus } from './types/importedInvoiceStatus'

export interface FetchImportedInvoicesParams {
  fromDate?: string
  page?: number
  pageSize?: number
  searchQuery?: string
  sortDirection?: string
  sortProperty?: string
  toDate?: string
}

export interface FetchImportedInvoicesResponseData extends Metable {
  documents: ImportedInvoiceDocument[]
}

export const fetchImportedInvoices = ({
  fromDate,
  page,
  pageSize,
  searchQuery: searchText,
  sortDirection,
  sortProperty,
  toDate,
}: FetchImportedInvoicesParams) => {
  const queryParams = qs.stringify({
    documentType: 'importedInvoice',
    ...(fromDate && toDate ? { fromDate, toDate } : {}),
    ...(page ? { page } : {}),
    ...(pageSize ? { pageSize } : {}),
    ...(searchText ? { searchText } : {}),
    ...(sortDirection ? { sortDirection } : {}),
    ...(sortProperty ? { sortProperty } : {}),
  })

  return apiZervant.restInvoice.get<FetchImportedInvoicesResponseData>(`/v1/documents/all?${queryParams}`)
}

// Flat, unlike the list's document summary: the sync fields sit at the top level rather than
// under a `status` block. `cpaToolsIntegrationRejectCode` stays an open string for the same
// reason as on `ImportedInvoiceDocumentStatus` — Cegid owns that vocabulary. `status` is the
// imported intake lifecycle status; only a `to_review` row can be approved (see
// `isImportedInvoiceApprovable`). Unlike the list, `fileId` and `uploadDate` are never null:
// they come from the row's NOT NULL `external_id` and `created_at` columns. `approved` stays null
// until the row is approved; the extraction keeps being served after that.
export interface FetchImportedInvoiceByIdResponseData {
  approved?: ImportedInvoiceApproved | null
  counterparty?: ImportedInvoiceCounterparty | null
  cpaToolsIntegrationRejectCode: string | null
  cpaToolsIntegrationStatus: CpaToolsIntegrationStatus | null
  fileId: string
  filename: string | null
  header?: ImportedInvoiceExtractionHeader | null
  importedInvoiceId: number
  lines?: ImportedInvoiceExtractionLine[] | null
  proposal?: ImportedInvoiceProposal | null
  status: ImportedInvoiceStatus
  uploadDate: string
}

export const fetchImportedInvoiceById = (importedInvoiceId: string) =>
  apiZervant.restInvoice.get<FetchImportedInvoiceByIdResponseData>(
    `/v1/external/imported-invoices/${importedInvoiceId}`,
  )

export interface ApproveImportedInvoiceLinePayload {
  accountId: string
  description?: string
  netAmount: number
  taxAmount: number
  taxRateId: string
}

/**
 * Amounts go out as JSON numbers. There is no line `type` (the server defaults it to `services`)
 * and no gross amount (the server computes it from the lines).
 */
export interface ApproveImportedInvoicePayload {
  /** The Billy contact id; the server looks the customer name up from it. */
  contactId: string
  currencyId: string
  dueDate?: string
  entryDate: string
  invoiceLines: ApproveImportedInvoiceLinePayload[]
  number: string
}

interface ApproveImportedInvoiceParams {
  importedInvoiceId: string
  payload: ApproveImportedInvoicePayload
}

/** Answers with the by-id body, `approved` filled in. A failed Cegid forward still answers 200. */
export const approveImportedInvoice = ({ importedInvoiceId, payload }: ApproveImportedInvoiceParams) =>
  apiZervant.restInvoice.post<FetchImportedInvoiceByIdResponseData>(
    `/v1/external/imported-invoices/${importedInvoiceId}/approve`,
    payload,
  )

export interface CreateImportedInvoiceParams {
  /** The billy-api File id (also the upload correlation key) of the stored upload. */
  fileId: string
  filename?: string
  /**
   * FE-computed auto-forward decision (`useIsAutoCegidIntegrationEnabled`).
   * rest-invoice honours this boolean as-is (Option B / FE pass-through): the
   * opt-out setting (`automaticCegidIntegration`) is not visible to rest-invoice,
   * so the FE owns the decision and rest-invoice never derives it.
   */
  autoForwardToCegid: boolean
}

export interface CreateImportedInvoiceResponseData {
  importedInvoiceId: number
  fileId: string
  filename: string | null
  status: ImportedInvoiceStatus
  autoForwarded: boolean
}

/**
 * Creates the imported (shoebox) document shell in rest-invoice from a stored
 * upload reply (STR-158 / S12). rest-invoice persists a `processing` row keyed by
 * `fileId` and, for auto-Cegid-eligible orgs (`autoForwardToCegid`), transitions
 * it to `accepted` and forwards it — otherwise it is stored only.
 */
export const createImportedInvoice = ({ fileId, filename, autoForwardToCegid }: CreateImportedInvoiceParams) =>
  apiZervant.restInvoice.post<CreateImportedInvoiceResponseData>('/v1/external/imported-invoices', {
    fileId,
    filename,
    autoForwardToCegid,
  })
